// 空间类型枚举
export enum SpaceType {
  ZONE = 'AREA',      // 区域
  SPACE = 'SPACE',    // 空间
  BUILDING = 'BUILDING' // 楼宇
}

// 空间状态枚举
export enum SpaceStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  MAINTENANCE = 'MAINTENANCE'
}

// 坐标类型枚举
export enum CoordinateType {
  TWO_D = 'TWO_D',
  THREE_D = 'THREE_D'
}

// 图纸类型枚举
export enum DrawingType {
  PLAN_2D = 'TWO_D_PLAN',
  MODEL_3D = 'THREE_D_MODEL'
}

// 标记类型
export enum MarkerType {
  DEVICE_POSITION = 'device_position',
  ALARM_POINT = 'alarm_point',
  INSPECTION_POINT = 'inspection_point'
}

// 2D坐标接口
export interface Coordinate2D {
  x: number;
  y: number;
}

// 3D坐标接口
export interface Coordinate3D extends Coordinate2D {
  z: number;
}

// 边界坐标（2D多边形）
export type Boundary2D = Array<[number, number]>;

// 边界坐标（3D立方体）
export interface Boundary3D {
  min: Coordinate3D;
  max: Coordinate3D;
}

// 空间主表接口
export interface Space {
  id: string;
  name: string;
  type: SpaceType;
  parentId?: string;
  parent?: Space;
  children?: Space[];
  spacePath?: string; // 空间路径，格式：aaa:bbb:ccc
  description?: string;
  status: SpaceStatus;
  orgId?: string; // 组织ID
  orgPath?: string; // 组织路径
  fromHotel?: boolean; // 是否来自酒店
  order: number; // 顺序值
  createdAt: string;
  updatedAt: string;

  // 关联数据
  coordinates?: SpaceCoordinate[];
  displays?: SpaceDisplay[];
  markers?: SpaceMarker[];
  drawings?: SpaceDrawing[];
  tags?: SpaceTag[];
  tagIds?: string[]; // 标签ID列表，用于表单

  // 统计信息
  _count?: {
    children: number;
    coordinates: number;
    markers: number;
  };
}

// 标签接口
export interface Tag {
  id: string;
  name: string;
  color?: string;
  icon?: string;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

// 空间标签接口
export interface SpaceTag {
  id: string;
  name: string;
  color?: string;
  icon?: string;
  description?: string;
}

// 标签表单接口
export interface TagFormData {
  name: string;
  color?: string;
  icon?: string;
  description?: string;
}

// 标签查询参数接口
export interface TagQueryParams extends Partial<PaginationParams> {
  keyword?: string;
  search?: string;
}

// 空间坐标接口
export interface SpaceCoordinate {
  id: string;
  spaceId: string;
  coordinateType: CoordinateType;
  coordinateSystem: string;
  origin?: Coordinate2D | Coordinate3D;
  boundary: Boundary2D | Boundary3D;
  center: Coordinate2D | Coordinate3D;
  isDefault: boolean;
}

// 显示样式接口
export interface DisplayStyle {
  fillColor?: string;
  borderColor?: string;
  opacity?: number;
  zIndex?: number;
}

// 标签配置接口
export interface LabelConfig {
  show: boolean;
  fontSize?: number;
  position?: 'top' | 'bottom' | 'left' | 'right' | 'center';
}

// 空间显示配置接口
export interface SpaceDisplay {
  id: string;
  spaceId: string;
  scene: string;
  style: DisplayStyle;
  icon?: string;
  label: LabelConfig;
  isDefault: boolean;
}

// 标记样式接口
export interface MarkerStyle {
  icon?: string;
  size?: number;
  color?: string;
}

// 空间标记接口
export interface SpaceMarker {
  id: string;
  spaceId: string;
  markerType: MarkerType;
  relatedId?: string;
  coordinate: Coordinate2D | Coordinate3D;
  style: MarkerStyle;
  description?: string;
  createdBy: string;
  createdAt: string;
}

// 空间图纸接口
export interface SpaceDrawing {
  id: string;
  spaceId: string;
  drawingType: DrawingType;
  fileUrl: string;
  version: string;
  scale: number;
  isActive: boolean;
}

// 空间树节点接口（用于树形组件）
export interface SpaceTreeNode {
  key: string;
  title: string;
  children?: SpaceTreeNode[];
  space: Space;
  isLeaf?: boolean;
}

// 空间表单接口
export interface SpaceFormData {
  name: string;
  type: SpaceType;
  parentId?: string;
  description?: string;
  status: SpaceStatus;
  tagIds?: string[];
  orgId?: string; // 组织ID
  orgPath?: string; // 组织路径
  fromHotel?: boolean; // 是否来自酒店
  order?: number; // 顺序值
  // spacePath 是自动生成的，不需要在表单中
}

// 坐标表单接口
export interface CoordinateFormData {
  coordinateType: CoordinateType;
  coordinateSystem: string;
  boundary: Boundary2D | Boundary3D;
  center: Coordinate2D | Coordinate3D;
  isDefault: boolean;
}

// API响应接口
export interface ApiResponse<T> {
  success: boolean;
  data: T | null;
  message?: string;
  error?: string;
  pagination?: PaginationInfo;
}

// 分页信息接口
export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// 分页接口
export interface PaginationParams {
  page: number;
  pageSize: number;
  total?: number;
}

// 查询参数接口
export interface SpaceQueryParams extends Partial<PaginationParams> {
  keyword?: string;
  type?: SpaceType;
  status?: SpaceStatus;
  parentId?: string;
  search?: string; // 后端使用search字段
  tags?: string[]; // 标签筛选
}

// ===== 认证相关接口 =====

// 登录数据接口
export interface LoginData {
  username?: string;
  email?: string;
  phoneNumber?: string;
  password: string;
  rememberMe?: boolean;
}

// 注册数据接口已移除，使用统一认证系统

// 移除认证响应接口

// 用户接口
export interface User {
  id: string;
  username: string;
  email: string;
  phone?: string;
  name?: string;
  avatar?: string;
  createdAt: string;
  updatedAt: string;
}

// ===== 微服务管理相关接口 =====

// 微服务接口
export interface MicroService {
  id: string;
  name: string; // 显示名称
  icon?: string; // 图标
  route: string; // 路由
  menuOrder: number; // 菜单排序
  isSystemService: boolean; // 是否为系统服务
  description?: string; // 描述
  status: 'ACTIVE' | 'INACTIVE'; // 状态
  createdAt: string;
  updatedAt: string;
}

// 微服务查询参数
export interface MicroServiceQueryParams {
  keyword?: string;
  status?: string;
  isSystemService?: boolean;
  page: number;
  limit: number;
}

// 微服务分页信息
export interface MicroServicePaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// ===== 权限管理相关接口 =====

// 权限接口
export interface Permission {
  id?: string;
  name: string;
  code?: string;
  resource: string;
  action: string;
  description?: string;
  type?: string;
  module?: string;
  system?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

// 角色接口
export interface Role {
  id: string;
  name: string;
  code?: string;
  description?: string;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

// 岗位接口
export interface Position {
  id: string;
  name: string;
  code?: string;
  organizationId: string;
  sortOrder?: number;
  memberCount?: number;
  status?: 'active' | 'inactive';
  createdAt?: string;
  updatedAt?: string;
  description?: string;
  level?: number;
}

// 枚举选项接口
export interface EnumOption {
  key: string;
  value: string;
  label: string;
}
